import { styled, useTheme } from "@mui/material/styles";
import {
  AUTH_ACTION,
  AUTH_PASSWORD_RESET,
  HOME,
  LOGIN,
  PASSWORD_RESET,
  PROTECTED,
  SIGNUP,
} from "constants/routes";
import { STATUS_PAGE_URL, ZENDESK_URL } from "constants/vars";
import { componentLoader } from "helpers/utilities";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import React, { Fragment, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router";

const AuthAction = lazy(() =>
  componentLoader(() => import("views/unprotected/AuthAction")),
);

const Login = lazy(() => componentLoader(() => import("views/auth/Login")));
const Signup = lazy(() => componentLoader(() => import("views/auth/Signup")));
const PasswordReset = lazy(() =>
  componentLoader(() => import("views/auth/PasswordReset")),
);
const Protected = lazy(() =>
  componentLoader(() => import("views/protected/Protected")),
);
const NotFound = lazy(() =>
  componentLoader(() => import("views/shared/NotFound")),
);

export default function App() {
  const theme = useTheme();

  useEffect(() => {
    if (document.querySelector(`script[src="${ZENDESK_URL}"]`)) {
      return;
    }
    const script = document.createElement("script");
    script.src = ZENDESK_URL;
    script.id = "ze-snippet";
    script.defer = true;
    script.onload = () => {
      if (
        (window.location.pathname.includes(AUTH_ACTION) ||
          window.location.pathname.includes(AUTH_PASSWORD_RESET)) &&
        window.zE !== undefined
      ) {
        window.zE("webWidget", "hide");
      }
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (document.querySelector(`script[src="${STATUS_PAGE_URL}"]`)) {
      return;
    }
    const script = document.createElement("script");
    script.src = STATUS_PAGE_URL;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: theme.palette.success.main,
    },
    "&.notistack-MuiContent-error": {
      backgroundColor: theme.palette.error.main,
    },
    "&.notistack-MuiContent-warning": {
      backgroundColor: theme.palette.warning.main,
    },
    "&.notistack-MuiContent-info": {
      backgroundColor: theme.palette.info.main,
    },
  }));

  return (
    <SnackbarProvider
      maxSnack={3}
      preventDuplicate
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
        info: StyledMaterialDesignContent,
      }}
    >
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <Suspense fallback={<Fragment />}>
          <Routes>
            <Route path={HOME} element={<Login />} />
            <Route path={LOGIN} element={<Login />} />
            <Route path={SIGNUP} element={<Signup />} />
            <Route path={PASSWORD_RESET} element={<PasswordReset />} />
            <Route path={AUTH_ACTION} element={<AuthAction />} />
            <Route path={PROTECTED}>
              <Route index path="*" element={<Protected />} />
            </Route>
            <Route path={"*"} element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </SnackbarProvider>
  );
}
